import moment from 'moment-timezone';
import { read_api_error } from '../../../utils/api_error_message';
import {
  class_start_ist_text,
  requested_at_to_iso
} from '../teacher_cancel_bucket_picker/bucket_rules';

// A-52: an Ops-entered request time that looks wrong is warned about, never blocked. apis is the
// authority (409 REQUEST_TIME_WARNING); the client pre-check only shows the step before the first send.

const IST = 'Asia/Kolkata';
export const REQUEST_TIME_WARNING = 'REQUEST_TIME_WARNING';
export const BEFORE_BOOKING = 'BEFORE_BOOKING';
export const OVER_7_DAYS = 'OVER_7_DAYS';
export const WARNING_CODES = [BEFORE_BOOKING, OVER_7_DAYS];
// Mirrors session_payment_meta.request_time_ack_note VARCHAR(500).
export const ACK_NOTE_MAX = 500;
const SEVEN_DAYS_MS = 7 * 24 * 3600 * 1000;
const MINUTE_MS = 60 * 1000;

export const DEFAULT_MESSAGES = {
  [BEFORE_BOOKING]: 'This time is before the class was booked.',
  [OVER_7_DAYS]: 'This is more than 7 days before the class.'
};

// Epoch ms of an epoch number or a server date string (a zone-less DB string is UTC); NaN if unknown.
export function to_ms(value) {
  if (value === null || value === undefined || value === '') return NaN;
  if (typeof value === 'number') return value > 0 ? value : NaN;
  if (value instanceof Date) return value.getTime();
  const m = moment.utc(String(value), [moment.ISO_8601, 'YYYY-MM-DD HH:mm:ss'], true);
  return m.isValid() ? m.valueOf() : NaN;
}

const iso_of = (ms) => (Number.isFinite(ms) ? new Date(ms).toISOString() : '');

// Mirrors apis request_time_warning: before the booking wins; then more than 7 days before the start.
// The booking is floored to its minute: Ops enter minutes, so the booking's own minute is not "before".
export function request_time_warning(requested_ms, class_start_ms, booked_at_ms) {
  if (!Number.isFinite(requested_ms)) return '';
  if (
    Number.isFinite(booked_at_ms) &&
    requested_ms < Math.floor(booked_at_ms / MINUTE_MS) * MINUTE_MS
  )
    return BEFORE_BOOKING;
  if (Number.isFinite(class_start_ms) && class_start_ms - requested_ms > SEVEN_DAYS_MS) {
    return OVER_7_DAYS;
  }
  return '';
}

// The 409 body as a warning, or null for any other error.
export function read_request_time_warning(e) {
  const { body, code, message } = read_api_error(e);
  if (code !== REQUEST_TIME_WARNING || !WARNING_CODES.includes(body.warning)) return null;
  return {
    warning: body.warning,
    booked_at: typeof body.booked_at === 'string' ? body.booked_at : '',
    class_start: typeof body.class_start === 'string' ? body.class_start : '',
    message: message || DEFAULT_MESSAGES[body.warning]
  };
}

// Client pre-check. Judged per class as the server does: any BEFORE_BOOKING class decides, else any
// OVER_7_DAYS one (the earliest such class supplies the facts). Pass `classes` [{class_start, booked_at}],
// or a single class_start/booked_at. null = nothing to warn about (the server may still warn).
export function client_request_time_warning({ requested_local, class_start, booked_at, classes }) {
  const requested_ms = to_ms(requested_at_to_iso(requested_local, IST));
  const list = (Array.isArray(classes) ? classes : [{ class_start, booked_at }])
    .map((c) => ({ start_ms: to_ms(c.class_start), booked_ms: to_ms(c.booked_at) }))
    .sort((a, b) => (a.start_ms || 0) - (b.start_ms || 0));
  const own = list.map((c) => ({
    ...c,
    warning: request_time_warning(requested_ms, c.start_ms, c.booked_ms)
  }));
  const hit = own.find((c) => c.warning === BEFORE_BOOKING) || own.find((c) => c.warning);
  if (!hit) return null;
  return {
    warning: hit.warning,
    booked_at: iso_of(hit.booked_ms),
    class_start: iso_of(hit.start_ms),
    message: DEFAULT_MESSAGES[hit.warning]
  };
}

// What the warning step shows: time facts only (owner rule: no payment wording in session dialogs).
export function warning_view(w, requested_local) {
  const booked_ms = to_ms(w.booked_at);
  const start_ms = to_ms(w.class_start);
  const requested = requested_local ? moment.tz(requested_local, IST) : null;
  return {
    title:
      w.warning === BEFORE_BOOKING
        ? 'Request time is before the class was booked'
        : 'Request time is more than 7 days before the class',
    message: w.message || DEFAULT_MESSAGES[w.warning] || '',
    requested_text:
      requested && requested.isValid()
        ? `Request time ${requested.format('D MMM YYYY, h:mm A')} IST`
        : '',
    booked_text: Number.isFinite(booked_ms)
      ? `Class booked ${moment(booked_ms).tz(IST).format('D MMM')}`
      : '',
    start_text: Number.isFinite(start_ms) ? class_start_ist_text(start_ms, IST, 'IST') : '',
    note_required: w.warning === BEFORE_BOOKING
  };
}

// '' = valid. BEFORE_BOOKING needs a 1-500 character note (it raises a review item); otherwise optional.
export function ack_note_error(warning, note) {
  const text = String(note || '').trim();
  if (warning === BEFORE_BOOKING && !text) return 'Write why this time is right (required).';
  if (text.length > ACK_NOTE_MAX) return `The note must be at most ${ACK_NOTE_MAX} characters.`;
  return '';
}

export function build_request_time_ack(warning, note) {
  const text = String(note || '').trim();
  return text ? { warning, note: text } : { warning };
}

export const with_request_time_ack = (payload, ack) =>
  ack ? { ...payload, request_time_ack: ack } : payload;

// ---- IST picker ----

// "Before this class was booked (25 Oct)" ('' when the booking time is unknown).
export function before_booking_hint(booked_at, iana_timezone = IST) {
  const ms = to_ms(booked_at);
  if (!Number.isFinite(ms)) return '';
  return `Before this class was booked (${moment(ms).tz(iana_timezone).format('D MMM')})`;
}

// The booking instant as a picker Date (local fields = the wall clock in iana_timezone), or null.
export function booked_picker_date(booked_at, iana_timezone = IST) {
  const ms = to_ms(booked_at);
  if (!Number.isFinite(ms)) return null;
  const m = moment(ms).tz(iana_timezone);
  return new Date(m.year(), m.month(), m.date(), m.hours(), m.minutes(), 0, 0);
}

// A calendar day wholly before the booking day.
export const is_day_before_booking = (day, booked) =>
  !!booked &&
  !!day &&
  new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime() <
    new Date(booked.getFullYear(), booked.getMonth(), booked.getDate()).getTime();

// A time slot (or a picked instant) before the booking minute.
export const is_time_before_booking = (time, booked) =>
  !!booked && !!time && time.getTime() < booked.getTime();

// When a one-off class was booked (session.created_date via the report's raw_data), or '' when it is
// a repeat-series class: the server measures those from the series' first class, which the page lacks.
export function session_booked_at(session_details) {
  const s = session_details || {};
  const raw = s.raw_data || {};
  if (Number(raw.repeat_session_id || s.repeat_session_id) > 0) return '';
  return raw.created_date || s.created_date || '';
}
